import { t } from '@/i18n/i18n'
import { AttendeesService } from '@/pages/attendee/attendees.service'
import { GuestsService } from '@/pages/guests/guests.service'
import router from '@/router'
import { ErrorService } from '@/shared/service/errorService'
import {
  FilesService,
  type ImageTransformations,
} from '@/shared/service/files.service'
import { normalizeStringForSearch } from '@/shared/service/utils'
import { SnackbarService } from '@/shared/snackbar/snackbar.service'
import { useAppDataStore } from '@/shared/store/app-data.store'
import {
  type Attendee,
  type AttendeeWithRateAndComment,
} from '@/shared/types/attendee.type'
import { type Pagination } from '@/shared/types/pagination.type'
import {
  type ProgEvent,
  ProgramEventNotifSendTime,
} from '@/shared/types/progEvent.type'
import { type Program } from '@/shared/types/program.type'
import { create } from '@/shared/utils/zustandVue'

import { cloneDeep, isNotNullish, type Optional } from '../../utils'
import { useGuestGenresStore } from '../guests/store/guestgenres.store'
import { ProgEventService, type ProgramEventUsage } from './progEvent.service'

export type ImportResult = {
  successes: ImportResultMessage[]
  errors: ImportResultMessage[]
  toProcess: ImportResultMessage[]
}
type ImportResultMessage = {
  key: string
  content: Record<string, unknown>
}

class ProgEventState {
  showMultipleDeleting = false
  showMultipleEdit = false
  progEventList: Record<string, ProgEvent> = {}
  loading = false
  deletingEventId: null | string = null
  gridMode = false

  selectedEventIdForDetails = ''
  attendeesList: AttendeeWithRateAndComment[] = []

  // Real defaults: UiDataTable divides by itemsPerPage (-1 would render all
  // rows) and page; the old v-data-table got these from :options.sync.
  // No default sort: Vue 2's sort-by="title" matched no column, so the list
  // showed the server (chronological) order until a header was clicked.
  options: Pagination = {
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
    sortDesc: [],
    groupBy: [],
    groupDesc: [],
    multiSort: false,
    mustSort: false,
  }

  search = {
    guest: '',
    program: '',
    scene: '',
    day: '',
  }
  selectedProgram: Program | null = null
  editingProgEvent: ProgEvent | null = null
  selectedRows: ProgEvent[] = []
  image: string | null = null
  transformations: ImageTransformations | null = null

  importBatchId = ''
  importFeedback: ImportResult = {
    successes: [],
    errors: [],
    toProcess: [],
  }

  togglingVisibilityEventId: null | string = null
  showVisibilityModal = false
  batchShowMode = null as null | boolean
  progEventUsage: ProgramEventUsage = {
    pageContents: [],
  }
}

type ProgEventStore = ProgEventState & {
  // Mutations → actions
  setProgEventList: (progEventList: ProgEvent[]) => void
  load: () => void
  unload: () => void
  setSearchGuest: (guest: string) => void
  setSearchProgram: (program: string) => void
  setSearchScene: (scene: string) => void
  setSearchDay: (day: string) => void
  setDeleting: (progEventId: string) => void
  setNotDeleting: () => void
  setShowMultipleDeleting: (show: boolean) => void
  setShowMultipleEditing: (show: boolean) => void
  resetMultiSelection: () => void
  changeDisplay: () => void
  setProgram: (program: Program) => void
  setImage: (fileName: string | null) => void
  setImageTransformations: (
    transformations: ImageTransformations | null,
  ) => void
  reset: () => void
  setEditing: (progEvent: ProgEvent) => void
  setDuplicate: (progEvent: ProgEvent) => void
  setOptions: (options: Pagination) => void
  setSelectedRows: (progEvents: ProgEvent[]) => void
  setEventIdForDetails: (id: string) => void
  resetEventIdForDetails: () => void
  setAttendeesList: (attendeesList: Attendee[]) => void
  setImportFeedback: (importResult: ProgEventState['importFeedback']) => void
  setImportBatchId: (importBatchId: string) => void
  closeVisibilityModal: () => void
  // Actions
  list: () => Promise<void>
  initHide: (programEventId: string) => Promise<void>
  hide: (programEventId: string) => Promise<void>
  delete: () => Promise<void>
  uploadImage: (file: File) => Promise<void>
  initHideShowBatch: (show: boolean) => Promise<void>
  hideShowBatch: (show: boolean) => Promise<void>
  editShowBatch: (payload: {
    programId?: string
    sceneId?: string
  }) => Promise<void>
  multipleDelete: () => Promise<void>
  addProgEvent: (progEvent: ProgEvent) => Promise<void>
  loadProgEvent: (data: {
    id: string
    loadType: 'edit' | 'duplicate'
  }) => Promise<void>
  loadAttendees: () => Promise<void>
  saveFile: (file: Blob, visible: boolean) => Promise<void>
  updateImportList: (batchId: string) => Promise<void>
  resetImport: () => void
}

// Default blank ProgEvent for the create form — intentionally incomplete;
// backend fills in _id and other server-generated fields. The shape is
// duplicated in `computeGetProgEvent` below (kept there because it is the
// actual value returned when no editing event is selected).

export const useProgEventStore = create<ProgEventStore>((set, get) => ({
  ...new ProgEventState(),

  // --- Mutations → actions ---

  setProgEventList: (progEventList) => {
    set({
      progEventList: progEventList.reduce(
        (acc: Record<string, ProgEvent>, progEvent) => {
          acc[progEvent._id] = progEvent
          return acc
        },
        {},
      ),
    })
  },

  load: () => {
    set({ loading: true })
  },

  unload: () => {
    set({ loading: false })
  },

  setSearchGuest: (guest) => {
    const state = get()
    set({
      search: { ...state.search, guest },
      options: { ...state.options, page: 1 },
    })
  },

  setSearchProgram: (program) => {
    const state = get()
    set({
      search: { ...state.search, program },
      options: { ...state.options, page: 1 },
    })
  },

  setSearchScene: (scene) => {
    const state = get()
    set({
      search: { ...state.search, scene },
      options: { ...state.options, page: 1 },
    })
  },

  setSearchDay: (day) => {
    const state = get()
    set({
      search: { ...state.search, day },
      options: { ...state.options, page: 1 },
    })
  },

  setDeleting: (progEventId) => {
    set({ deletingEventId: progEventId })
  },

  setNotDeleting: () => {
    set({ deletingEventId: null })
  },

  setShowMultipleDeleting: (show) => {
    set({ showMultipleDeleting: show })
  },

  setShowMultipleEditing: (show) => {
    set({ showMultipleEdit: show })
  },

  resetMultiSelection: () => {
    set({ selectedRows: [] })
  },

  changeDisplay: () => {
    set({ gridMode: !get().gridMode })
  },

  setProgram: (program) => {
    set({ selectedProgram: program })
  },

  setImage: (fileName) => {
    set({ image: fileName })
  },

  setImageTransformations: (transformations) => {
    set({ transformations })
  },

  reset: () => {
    // Partial reset (Vue 2 RESET parity): only the edition state. Filters,
    // pagination, grid mode/program must survive leaving the edit page so
    // /programmation comes back as the user left it.
    set({ image: null, editingProgEvent: null, transformations: null })
    useGuestGenresStore.getState().reset()
  },

  setEditing: (progEvent) => {
    set({ editingProgEvent: progEvent, image: progEvent.image ?? null })
  },

  setDuplicate: (progEvent) => {
    const { _id: _removed, ...copy } = cloneDeep(progEvent)
    void _removed
    set({ editingProgEvent: copy as ProgEvent, image: copy.image ?? null })
  },

  setOptions: (options) => {
    set({ options })
  },

  setSelectedRows: (progEvents) => {
    set({ selectedRows: progEvents })
  },

  setEventIdForDetails: (id) => {
    set({ selectedEventIdForDetails: id })
  },

  resetEventIdForDetails: () => {
    set({ selectedEventIdForDetails: '', attendeesList: [] })
  },

  setAttendeesList: (attendeesList) => {
    const state = get()
    set({
      attendeesList: attendeesList.map((attendee) => {
        const tmp = state.progEventList[
          state.selectedEventIdForDetails
        ].eventAttendeesList.filter(
          (a) => a.firebaseUserId === attendee.firebaseUserId,
        )
        const newAttendee: AttendeeWithRateAndComment = {
          ...attendee,
        } as AttendeeWithRateAndComment
        newAttendee.comment = tmp.at(0)?.comment ?? ''
        newAttendee.rate = tmp.at(0)?.rate ?? -1
        return newAttendee
      }),
    })
  },

  setImportFeedback: (importResult) => {
    set({ importFeedback: importResult })
  },

  setImportBatchId: (importBatchId) => {
    set({ importBatchId })
  },

  closeVisibilityModal: () => {
    set({
      showVisibilityModal: false,
      togglingVisibilityEventId: null,
      progEventUsage: { pageContents: [] },
    })
  },

  // --- Actions ---

  async list() {
    get().load()
    try {
      const progEventList = await ProgEventService.getAll()
      get().setProgEventList(progEventList)
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async initHide(programEventId) {
    const state = get()
    const usage = state.progEventList[programEventId].isVisible
      ? await ProgEventService.checkUsage([programEventId])
      : {
          pageContents: [],
        }
    if (usage.pageContents.length > 0) {
      set({
        togglingVisibilityEventId: programEventId,
        progEventUsage: usage,
        showVisibilityModal: true,
      })
    } else {
      await get().hide(programEventId)
    }
  },

  async hide(programEventId) {
    get().load()
    try {
      const programEvent = cloneDeep(get().progEventList[programEventId])
      programEvent.isVisible = !programEvent.isVisible
      await ProgEventService.updateProgramEvent(programEvent)
      const message = programEvent.isVisible
        ? t('PROGRAMMATION.SNACK_BAR.HIDE_OFF', {
            string: programEvent.titles?.fr ?? programEvent.fakeTitle,
          })
        : t('PROGRAMMATION.SNACK_BAR.HIDE', {
            string: programEvent.titles?.fr ?? programEvent.fakeTitle,
          })
      SnackbarService.info(message)
      await get().list()
      // the preview keeps the grab data fetched when its sidebar mounted, so it
      // would go on showing what we just hid until something reloads it
      await useAppDataStore
        .getState()
        .loadCurrentData({ filterOnlyVisible: true })
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().closeVisibilityModal()
    }
  },

  async delete() {
    get().load()
    try {
      const { deletingEventId } = get()
      if (deletingEventId == null) return
      await ProgEventService.deleteProgramEvent(deletingEventId)
      const progEvent = get().progEventList[deletingEventId]
      SnackbarService.info(
        t('PROGRAMMATION.SNACK_BAR.DELETE', {
          string: progEvent.titles?.fr ?? progEvent.fakeTitle,
        }),
      )
      await get().list()
      get().setNotDeleting()
    } catch (e) {
      ErrorService.handleError(e)
    }
  },

  async uploadImage(file) {
    get().load()
    try {
      const savedImage = await FilesService.uploadImageFile(file)
      get().setImage(savedImage.filename)
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async initHideShowBatch(show) {
    const { selectedRows } = get()
    const usage = !show
      ? await ProgEventService.checkUsage(selectedRows.map((e) => e._id))
      : {
          pageContents: [],
        }
    if (usage.pageContents.length > 0) {
      set({
        progEventUsage: usage,
        showVisibilityModal: true,
        batchShowMode: show,
      })
    } else {
      await get().hideShowBatch(show)
    }
  },

  async hideShowBatch(show) {
    get().load()
    try {
      const { selectedRows } = get()
      const nb = selectedRows.length
      await ProgEventService.hideShowBatch(
        selectedRows.map((e) => e._id),
        show,
      )
      await get().list()
      await useAppDataStore
        .getState()
        .loadCurrentData({ filterOnlyVisible: true })
      if (show) {
        SnackbarService.info(
          t('PROGRAMMATION.SNACK_BAR.BATCH_SHOW', {
            string: nb,
          }),
        )
      } else {
        SnackbarService.info(
          t('PROGRAMMATION.SNACK_BAR.BATCH_HIDE', {
            string: nb,
          }),
        )
      }
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().resetMultiSelection()
      get().unload()
      get().closeVisibilityModal()
    }
  },

  async editShowBatch({ programId, sceneId }) {
    get().load()
    try {
      const { selectedRows } = get()
      const nb = selectedRows.length
      await ProgEventService.editShowBatch(
        selectedRows.map((e) => e._id),
        programId,
        sceneId,
      )
      await get().list()
      SnackbarService.info(
        t('PROGRAMMATION.SNACK_BAR.BATCH_EDIT', {
          string: nb,
        }),
      )
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().resetMultiSelection()
      get().unload()
    }
  },

  async multipleDelete() {
    get().load()
    try {
      const { selectedRows } = get()
      const nb = selectedRows.length
      await ProgEventService.multipleDelete(selectedRows.map((e) => e._id))
      await get().list()
      SnackbarService.info(
        t('PROGRAMMATION.SNACK_BAR.MULTIPLE_DELETE', {
          string: nb,
        }),
      )
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().setShowMultipleDeleting(false)
      get().resetMultiSelection()
    }
  },

  async addProgEvent(progEvent) {
    get().load()
    try {
      const { image, editingProgEvent, transformations } = get()
      const event = cloneDeep(progEvent)
      event.image = image
      // event.genres is already populated correctly from local TagManager in AddingProgEvent
      if (event.musicGroups) {
        event.musicGroups = await Promise.all(
          event.musicGroups.map(async (group) => {
            return await GuestsService.getGuestById(group._id ?? '')
          }),
        )
      }

      const isEditing = Boolean(editingProgEvent && event._id)
      let message = ''
      if (isEditing) {
        await ProgEventService.updateProgramEvent(event)
        message = t('PROGRAMMATION.SNACK_BAR.EDIT', {
          string: event.titles?.fr ?? event.fakeTitle,
        })
      } else {
        await ProgEventService.addProgramEvent(event)
        message = t('PROGRAMMATION.SNACK_BAR.ADD', {
          string: event.titles?.fr ?? event.fakeTitle,
        })
      }

      if (transformations) {
        await FilesService.setImageTransform(
          transformations.imageId,
          transformations,
        )
      }
      SnackbarService.info(message)
      await get().list()
      await useAppDataStore
        .getState()
        .loadCurrentData({ filterOnlyVisible: true })
      await router.push('/programmation')
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async loadProgEvent(data) {
    try {
      await useGuestGenresStore.getState().getGenres()
      const progEvent = await ProgEventService.getProgramEventById(data.id)

      // Hydrate tags for frontend TagManager (which expects objects, not IDs)
      if (progEvent.genres) {
        const guestGenresState = useGuestGenresStore.getState()
        progEvent.tags = progEvent.genres
          .map((genre: string) => cloneDeep(guestGenresState.genres[genre]))
          .filter(isNotNullish)
      }

      if (data.loadType === 'edit') {
        get().setEditing(progEvent)
      } else {
        get().setDuplicate(progEvent)
      }
      if (progEvent.image != null && progEvent.image !== '') {
        const transformations = await FilesService.getImageTransform(
          progEvent.image,
        )
        get().setImageTransformations(transformations)
      }
    } catch (e) {
      ErrorService.handleError(e)
    }
  },

  async loadAttendees() {
    try {
      get().load()
      const state = get()
      const attendeesList = await AttendeesService.getAllByFirebaseUserIds(
        state.progEventList[
          state.selectedEventIdForDetails
        ].eventAttendeesList.map((e) => e.firebaseUserId),
      )

      get().setAttendeesList(attendeesList)
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async saveFile(file, visible) {
    get().load()
    const { promise, resolve, reject } = Promise.withResolvers<
      string | ArrayBuffer | null
    >()
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = (): void => {
      resolve(reader.result)
    }
    reader.onerror = (error): void => {
      console.error(error)
      reject(error instanceof Error ? error : new Error('Error reading file'))
    }
    try {
      const x64File = await promise
      const batchId = await ProgEventService.uploadFile(x64File, visible)
      get().setImportBatchId(batchId)
      await get().updateImportList(batchId)
    } catch (e) {
      ErrorService.handleError(e)
      get().unload()
    }
  },

  async updateImportList(batchId) {
    const importResult = await ProgEventService.getImportResult(batchId)
    get().setImportFeedback(importResult)
    if (importResult.toProcess.length > 0) {
      const { promise, resolve } = Promise.withResolvers<undefined>()
      setTimeout(resolve, 7000)
      await promise
      await get().updateImportList(batchId)
    } else {
      get().unload()
    }
  },

  resetImport: () => {
    set({
      importFeedback: { successes: [], errors: [], toProcess: [] },
      importBatchId: '',
    })
  },
}))

// --- Computed getter helpers (replacements for the removed useGetterProgEvent
// wrapper). Each takes the reactive store proxy (useProgEventStore()) so
// consumers can wrap them in `computed(() => compute*(store))`.

export function computeGetProgEventList(state: ProgEventState): ProgEvent[] {
  return Object.values(state.progEventList)
}

export function computeGetProgEvent(
  state: ProgEventState,
): Optional<
  ProgEvent,
  | '_id'
  | 'attendees'
  | 'eventAttendeesList'
  | 'fakeTitle'
  | 'date'
  | 'meanRate'
  | 'soldOut'
  | 'hideEndDate'
  | 'hideLocation'
  | 'ticketOpenInWebview'
> {
  return state.editingProgEvent
    ? state.editingProgEvent
    : {
        titles: null,
        image: null,
        copyright: null,
        descriptions: { fr: '', en: '' },
        lineUp: { fr: '', en: '' },
        prices: { fr: '', en: '' },
        genres: [],
        eventTicketLink: null,
        startTime: '12:00',
        endTime: '13:00',
        day: '',
        scene: {
          _id: '',
          key: '',
          names: { fr: '', en: '' },
          address: '',
          sceneImgGedFileId: '',
          color: '',
          weight: 0,
          program: {
            _id: '',
            names: { fr: '', en: '' },
            color: '',
            isDeletable: false,
          },
          freeze: false,
        },
        program: {
          _id: '',
          names: { fr: '', en: '' },
          color: '',
          isDeletable: false,
        },
        musicGroups: [],
        isVisible: false,
        enableAttending: false,
        enableRating: false,
        programEventNotifSendTime: ProgramEventNotifSendTime.NEVER,
        notifPlanned: false,
        keepGuestTags: null,
      }
}

export function computeGetFilteredEventList(
  state: ProgEventState,
): ProgEvent[] {
  let filteredEvents = cloneDeep(computeGetProgEventList(state))
  if (state.search.guest !== '') {
    filteredEvents = filteredEvents.filter(
      (n) =>
        (!!n.musicGroups &&
          n.musicGroups.filter((group) =>
            normalizeStringForSearch(group.names.fr).includes(
              normalizeStringForSearch(state.search.guest),
            ),
          ).length > 0) ||
        (!!n.titles &&
          normalizeStringForSearch(n.titles.fr).includes(
            normalizeStringForSearch(state.search.guest),
          )),
    )
  }
  if (state.search.program !== '') {
    filteredEvents = filteredEvents.filter(
      (n) => n.program._id === state.search.program,
    )
  }
  if (state.search.scene !== '') {
    filteredEvents = filteredEvents.filter(
      (n) => n.scene._id === state.search.scene,
    )
  }
  if (state.search.day !== '') {
    filteredEvents = filteredEvents.filter((n) => n.day === state.search.day)
  }
  return filteredEvents
}

export function computeGetDays(state: ProgEventState): string[] {
  return Object.values(state.progEventList)
    .reduce((acc: string[], event) => {
      if (!acc.includes(event.day)) acc.push(event.day)
      return acc
    }, [])
    .sort((a, b) => (a > b ? 1 : a < b ? -1 : 0))
}

export function computeGetDaysByProgram(
  state: ProgEventState,
): (programId: string) => string[] {
  return (programId: string) => {
    const tab = cloneDeep(
      Object.values(state.progEventList).filter(
        (n) => n.program._id === programId,
      ),
    )
    return tab
      .reduce((acc: string[], event) => {
        if (!acc.includes(event.day)) acc.push(event.day)
        return acc
      }, [])
      .sort((a, b) => (a > b ? 1 : a < b ? -1 : 0))
  }
}

export function computeGetSelectedProgram(
  state: ProgEventState,
  fallbackPrograms: Program[],
): Program {
  return state.selectedProgram ? state.selectedProgram : fallbackPrograms[0]
}

export function computeGetItems(state: ProgEventState): (
  selectedProgram: Program,
) => (Record<string, ProgEvent[] | string | undefined> & {
  scene: string
})[] {
  return (selectedProgram: Program) => {
    const events = cloneDeep(
      Object.values(state.progEventList).filter(
        (p) => p.program._id === selectedProgram._id,
      ),
    )
    const listScene = events.reduce(
      (acc: { _id: string; name: string }[], event) => {
        if (acc.filter((s) => s._id === event.scene._id).length === 0) {
          acc.push({ _id: event.scene._id, name: event.scene.names.fr })
        }
        return acc
      },
      [],
    )
    return listScene.reduce(
      (
        acc: (Record<string, ProgEvent[] | string | undefined> & {
          scene: string
        })[],
        scene,
      ) => {
        const obj = {} as Record<string, ProgEvent[] | string | undefined> & {
          scene: string
        }
        obj.scene = scene.name
        events
          .filter((n) => n.scene._id === scene._id)
          .map((event) => {
            if (obj[event.day] == null) obj[event.day] = []
            ;(obj[event.day] as ProgEvent[]).push(event)
            return event
          })
        acc.push(obj)
        return acc
      },
      [],
    )
  }
}

export function computeGetProgEventDetail(state: ProgEventState): ProgEvent {
  return state.progEventList[state.selectedEventIdForDetails]
}
